<template>
  <div class="tw-relative">
    <div class="tw-grid tw-grid-cols-12 max-lg:tw-grid-cols-1 tw-gap-6 tw-pb-8">
      <div class="tw-col-span-12 tw-h-full tw-flex tw-items-center">
        <div class="tw-w-full tw-h-full tw-rounded-sm tw-bg-white tw-p-4">
          <div class="tw-grid tw-grid-cols-9 tw-gap-3">
            <div class="tw-col-start-1 tw-col-end-3">
              {{ t("solution-label") }}
            </div>
            <div class="tw-col-start-1 tw-col-end-3 tw-pb-1.5">
              <cl-select
                v-model="selectedServiceUuid"
                test-id="customer-license-usage-select-solution"
                :is-disabled="filteredServices.length <= 1"
                @update:modelValue="selectService"
              >
                <cl-select-item
                  v-for="option in filteredServices"
                  :value="option.value"
                  :text-value="option.text"
                >
                  {{ option.text }}</cl-select-item
                >
              </cl-select>
            </div>
          </div>
          <usage-chart
            :id="selectedCustomer?.uuid || selectedAccount?.uuid"
            :selected-service-licence-uuid="selectedServiceLicenceUuid"
            data-test-id="customer-license-usage-usage-chart"
          />
        </div>
      </div>
    </div>
    <header>
      <cl-heading :size="'h4'">
        {{ t("protected-mailboxes") }}
      </cl-heading>
    </header>
    <section class="all-customers">
      <customers-table />
    </section>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useAuthenticationStore } from "@/store/authentication.store";
import { useI18n } from "vue-i18n";
import { usePageMetaStore } from "@/store/pageMeta.store";
import { useOrganisationsStore } from "@/store/organisations.store";

import UsageChart from "@/modules/Usage/views/Customer/Overview/UsageChart/index.vue";
import { ServiceOption } from "@/types/serviceUsage.ts";
import Cookies from "universal-cookie";
import { CHART_COOKIE } from "@/constants/chart-cookie";
import { ALL_SERVICES } from "@/utils/services.ts";
import { trackEvent } from "@/utils/track-event";
import CustomersTable from "@/modules/Usage/views/Customer/Overview/CustomersTable/CustomersTable.vue";

const { t } = useI18n();
const authStore = useAuthenticationStore();
const organisationsStore = useOrganisationsStore();
const pageMetaStore = usePageMetaStore();
const loading = ref<boolean>(true);

const selectedCustomer = computed(() => authStore.selectedCustomer);
const selectedAccount = computed(() => authStore.selectedAccount);

const selectedServiceUuid = ref<string>("");
const selectedSolutionName = ref<string>("");
const selectedServiceLicenceUuid = ref<string>("");
const allServices = ref<ServiceOption[]>([]);
const filteredServices = ref<ServiceOption[]>([]);

const selectService = async (serviceUuid: string) => {
  const selectedService = filteredServices.value.find(
    (service: ServiceOption) => service.value === serviceUuid,
  );

  selectedServiceUuid.value = selectedService?.value ?? "";
  selectedSolutionName.value = selectedService?.text ?? "";
  selectedServiceLicenceUuid.value = selectedService?.licence_uuid ?? "";

  const cookies = new Cookies();
  cookies.set(
    `${CHART_COOKIE.CUSTOMER_USAGE_CHART_SELECTED_SERVICE}_${selectedCustomer.value?.uuid || selectedAccount.value?.uuid}`,
    serviceUuid,
  );

  trackEvent({
    category: "Licence Usage Customer",
    action: "Service select",
    name: selectedService?.text || "",
  });
};

const getOrganisationServices = async () => {
  loading.value = true;
  const organisationResponse = (await organisationsStore.showOrg({
    uuid: selectedCustomer?.value?.uuid || selectedAccount?.value?.uuid || "",
  }))!;

  if (
    Array.isArray(organisationResponse.data.licence?.solutions) &&
    organisationResponse.data.licence.solutions.length > 0 &&
    Array.isArray(organisationResponse.data.licence.solutions[0]) &&
    organisationResponse.data.licence.solutions[0].length > 0
  ) {
    filteredServices.value = allServices.value
      .filter((service: ServiceOption) =>
        organisationResponse.data.licence.solutions[0].some(
          (customerService: { solution_uuid: string }) =>
            customerService.solution_uuid === service.value,
        ),
      )
      .map((service: ServiceOption) => {
        const matchingService =
          organisationResponse.data.licence.solutions[0].find(
            (customerService: { solution_uuid: string }) =>
              customerService.solution_uuid === service.value,
          );
        return {
          ...service,
          licence_uuid: matchingService?.uuid,
          value: matchingService?.solution_uuid,
        };
      });
  }
  loading.value = false;
};

onMounted(async () => {
  pageMetaStore.setPageTitle({
    title: t("license-usage"),
  });

  allServices.value = ALL_SERVICES.map(
    (service: { uuid: string; menuItemLabel: string }) => {
      return {
        value: service.uuid,
        text: t(service.menuItemLabel),
      };
    },
  );

  await getOrganisationServices();

  const cookies = new Cookies();
  const cookieValue = cookies.get(
    `${CHART_COOKIE.CUSTOMER_USAGE_CHART_SELECTED_SERVICE}_${selectedCustomer.value?.uuid || selectedAccount.value?.uuid}`,
  );

  const cookieSelectedService = filteredServices.value.find(
    (service: ServiceOption) => service.value === cookieValue,
  );

  const selectedService = cookieSelectedService ?? filteredServices.value?.[0];

  selectedServiceUuid.value = selectedService?.value ?? "";
  selectedSolutionName.value = selectedService?.text ?? "";
  selectedServiceLicenceUuid.value = selectedService?.licence_uuid ?? "";

  if (selectedService) {
    trackEvent({
      category: "Licence Usage Customer",
      action: "Page loaded with pre-selected service",
      name: selectedService.text,
    });
  }
});
</script>
